/** URL helpers for embedded launcher iframes. Lobby ↔ launcher IPC uses WebWrapper RPC only. */

import { ddRumError } from '@/utils/datadog';
import { GAME_PLAY_PATH_PREFIX } from '@/utils/url/game-play-path';

/** Reads the `gameUid` out of a short-link launcher path (`<prefix>/<gameUid>`). */
const extractPathGameUid = (pathname: string, pathPrefix: string): string | undefined => {
    const normalized = pathname.replace(/\/$/, '');
    const prefix = `${pathPrefix}/`;

    if (!normalized.startsWith(prefix) || normalized.length <= prefix.length) {
        return undefined;
    }

    const pathGameUid = normalized.slice(prefix.length);

    return pathGameUid && !pathGameUid.includes('/') ? pathGameUid : undefined;
};

/**
 * A full FGP (FanDuel Game Platform) query launch carries everything the launcher needs to fetch
 * the game, so it detaches as a query URL rather than as a short link. Mirrors the same check in
 * the launcher's own `src/logic/detached-session.js`.
 */
const isFgpQueryLaunch = (source: URL): boolean =>
    source.searchParams.has('providerName') && source.searchParams.has('gameId');

/**
 * Whether the query string identifies a game at all. Incentive-games launches are query launches
 * with neither `gameId` nor `gameUid` (see `getLauncherUrl`), so they are launchable without being
 * an FGP query launch.
 */
const hasLaunchableQuery = (source: URL): boolean =>
    source.searchParams.has('providerName') || source.searchParams.has('gameUid');

const withDetachHandoffParams = (url: URL, transferId: string): string => {
    url.searchParams.set('mode', 'detached');
    url.searchParams.set('transferId', transferId);
    return url.toString();
};

export const appendEmbeddedQueryParam = (launcherUrl: string): string => {
    const url = new URL(launcherUrl, window.location.origin);
    url.searchParams.set('embedded', '1');
    return url.toString();
};

/**
 * Builds the URL the detached (popped-out) launcher window opens at.
 *
 * Games whose data carries no provider/rgi — every game on the category-v2 hub pages — launch
 * through the short-link form `<prefix>/<gameUid>`. The launcher resolves a uid from the *path*
 * only (`<Route path="/:gameUid">`); at a bare `<prefix>/` it takes the query-launch branch, which
 * needs `providerName`/`gameId`/`providerRef` to fetch the game. Flattening the uid into
 * `?gameUid=` therefore produces a window that can never load, so a short-link source has to stay
 * a short link here.
 */
export const buildDetachedLauncherUrl = (
    transferId: string,
    sourceLauncherUrl?: string | null,
    pathPrefix: string = GAME_PLAY_PATH_PREFIX
): string => {
    const basePath = `${window.location.origin}${pathPrefix}/`;

    if (!sourceLauncherUrl) {
        return withDetachHandoffParams(new URL(basePath), transferId);
    }

    let source: URL;

    try {
        source = new URL(sourceLauncherUrl, window.location.origin);
    } catch {
        // Ignore invalid launcher URLs and fall back to the base detached route.
        return withDetachHandoffParams(new URL(basePath), transferId);
    }

    const pathGameUid = extractPathGameUid(source.pathname, pathPrefix);
    const queryGameUid = source.searchParams.get('gameUid');
    const shortLinkGameUid = pathGameUid ?? (isFgpQueryLaunch(source) ? null : queryGameUid);

    if (shortLinkGameUid) {
        return withDetachHandoffParams(new URL(`${basePath}${encodeURIComponent(shortLinkGameUid)}`), transferId);
    }

    const url = new URL(basePath);

    source.searchParams.forEach((value, key) => {
        if (key !== 'embedded' && key !== 'mode' && key !== 'transferId') {
            url.searchParams.set(key, value);
        }
    });

    if (!hasLaunchableQuery(source)) {
        // Neither a short link nor a query that identifies a game: nothing for the window to launch.
        ddRumError({
            name: 'Undetachable launcher URL',
            description: 'Could not derive a launchable detached URL from the source launcher URL',
            context: {
                pathname: source.pathname,
                prefix: `${pathPrefix}/`,
                error: new Error('Undetachable launcher URL')
            },
            use_case: 'GLOBAL'
        });
    }

    return withDetachHandoffParams(url, transferId);
};
